import type { QueryClient } from "@tanstack/react-query";

import { removeTrigger, replaceTrigger } from "../triggerList";
import type { TriggerV2Record } from "../TriggerV2Record";

import { triggersV2QueryKeys } from "./triggersV2QueryKeys";

/**
 * Writes a trigger the server returned to its own query and, in place of the
 * trigger named `nameBefore`, to the org's cached list, then refetches both.
 * A list that isn't cached is left alone.
 *
 * The refetch cancels a fetch already in flight without rolling it back, so
 * neither that fetch nor a later rollback (as on unmount) can undo the write.
 */
export function storeTrigger(
  queryClient: QueryClient,
  orgId: string,
  nameBefore: string,
  record: TriggerV2Record,
): Promise<unknown> {
  const byName = triggersV2QueryKeys.byName(orgId, record.name);
  const list = triggersV2QueryKeys.list(orgId);
  queryClient.setQueryData(byName, record);
  queryClient.setQueryData<readonly TriggerV2Record[]>(list, (cached) =>
    cached === undefined
      ? undefined
      : replaceTrigger(cached, nameBefore, record),
  );
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: byName }),
    queryClient.invalidateQueries({ queryKey: list }),
  ]);
}

/**
 * Removes a deleted trigger from its own query and from the org's cached
 * list, then refetches the list. A list that isn't cached is left alone.
 */
export function forgetTrigger(
  queryClient: QueryClient,
  orgId: string,
  name: string,
): Promise<void> {
  const list = triggersV2QueryKeys.list(orgId);
  queryClient.removeQueries({
    queryKey: triggersV2QueryKeys.byName(orgId, name),
  });
  queryClient.setQueryData<readonly TriggerV2Record[]>(list, (cached) =>
    cached === undefined ? undefined : removeTrigger(cached, name),
  );
  return queryClient.invalidateQueries({ queryKey: list });
}
